/**
 * VAT Selector Component
 *
 * A component for selecting Swiss VAT rates (0%, 2.6%, 8.1%)
 * with support for AI suggestions and user overrides.
 */

import { renderIcon } from '../utils/icons.js';

/**
 * Swiss VAT rates as defined by ESTV
 */
export const SWISS_VAT_RATES = {
  EXEMPT: 0,
  REDUCED: 2.6,
  STANDARD: 8.1,
} as const;

export interface VatSelectorOptions {
  selectedRate: number;
  aiSuggestedRate?: number;
  onChange: (rate: number) => void;
  disabled?: boolean;
}

/**
 * Create a VAT selector component with Swiss VAT rate options
 *
 * @param options Configuration for the VAT selector
 * @returns HTMLElement containing the VAT selector
 */
export function createVatSelector(options: VatSelectorOptions): HTMLElement {
  const container = document.createElement('div');
  container.className = 'flex flex-col gap-2';

  if (options.disabled) {
    container.classList.add('opacity-50');
  }

  // Create fieldset for proper grouping
  const fieldset = document.createElement('fieldset');
  fieldset.setAttribute('role', 'radiogroup');
  fieldset.className = 'border-0 p-0';

  // Add legend for accessibility (visually hidden)
  const legend = document.createElement('legend');
  legend.id = `vat-selector-legend-${Math.random().toString(36).substr(2, 9)}`;
  legend.className = 'sr-only';
  legend.textContent = 'Select VAT Rate';
  fieldset.setAttribute('aria-labelledby', legend.id);
  fieldset.appendChild(legend);

  // VAT rate labels
  const rateLabels: Record<string, string> = {
    '0': 'Exempt',
    '2.6': 'Reduced',
    '8.1': 'Standard',
  };

  // Create radio buttons for each VAT rate
  Object.entries(SWISS_VAT_RATES).forEach(([key, rate]) => {
    const labelContainer = document.createElement('label');
    labelContainer.className = 'flex items-center gap-3 p-2 rounded-md hover:bg-neutral-800/50 cursor-pointer transition-colors';

    // Create unique ID for this radio
    const radioId = `vat-rate-${rate}-${Math.random().toString(36).substr(2, 9)}`;

    // Radio input
    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.id = radioId;
    radio.name = `vat-rate-${Math.random().toString(36).substr(2, 9)}`; // Unique name per selector instance
    radio.value = String(rate);
    radio.checked = rate === options.selectedRate;
    radio.disabled = options.disabled || false;
    radio.className = 'w-4 h-4 text-purple-500 bg-neutral-800 border-neutral-600 focus:ring-purple-500 focus:ring-2';

    // Only add change handler if not currently selected
    radio.onchange = () => {
      const newRate = parseFloat(radio.value);
      if (newRate !== options.selectedRate) {
        options.onChange(newRate);
      }
    };

    labelContainer.setAttribute('for', radioId);
    labelContainer.appendChild(radio);

    // Rate text
    const rateText = document.createElement('span');
    rateText.className = 'flex-1 text-sm text-neutral-200';
    rateText.textContent = `${rate}% - ${rateLabels[String(rate)] || key}`;
    labelContainer.appendChild(rateText);

    // AI suggestion indicator
    if (options.aiSuggestedRate === rate) {
      const badge = document.createElement('span');
      badge.className = 'badge-ai text-xs px-2 py-1 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30';
      badge.setAttribute('data-ai-suggested', String(rate));

      const aiIcon = renderIcon('checkCircle', { size: 'xs' });
      aiIcon.style.display = 'inline-block';
      aiIcon.style.marginRight = '4px';

      badge.appendChild(aiIcon);
      badge.appendChild(document.createTextNode('AI suggested'));
      labelContainer.appendChild(badge);
    }

    fieldset.appendChild(labelContainer);
  });

  container.appendChild(fieldset);

  // Ensure the same radio name for all radios in this instance
  const radios = container.querySelectorAll('input[type="radio"]');
  if (radios.length > 0) {
    const groupName = (radios[0] as HTMLInputElement).name;
    radios.forEach(radio => {
      (radio as HTMLInputElement).name = groupName;
    });
  }

  return container;
}